<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	import type { Device } from 'src/types/Device';
	import type { Peripheral } from 'src/types/Peripherals';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { SerialConfig } from 'src/types/SerialConfig';
	import { Select, SliderV2, Text, Toggle } from '@Shelly/shelly-design-system';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { SERIAL_MODE_LABELS } from 'src/helpers/addons/addon_serial_helpers';

	export let device: Device;
	export let peripheral: Peripheral;
	export let settings: SerialConfig;

	const STANDARD_BAUDS = [
		9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600, 1_000_000, 2_000_000,
	];
	const BAUD_LABELS = new Set([9600, 115200, 2_000_000]);
	const STANDARD_FORMATS = ['8N1', '8E1', '8O1', '8N2', '7E1', '7O1', '7N1'];

	const component = peripheral.sensor.component;
	let supportedModes: string[] = [];

	$: currentMode = settings?.mode ?? '';
	$: currentBaud = settings?.serial?.baud ?? 115200;
	$: currentFormat = settings?.serial?.format ?? '8N1';
	$: currentHd = settings?.serial?.hd;
	$: currentDeAl = settings?.serial?.de_al;

	$: baudValues = [...new Set([...STANDARD_BAUDS, currentBaud])]
		.filter(Number.isFinite)
		.sort((a, b) => a - b);
	$: baudIndex = Math.max(0, baudValues.indexOf(currentBaud));
	$: baudLineMarkers = baudValues.map((value, i) => ({
		value: i,
		showLabel: BAUD_LABELS.has(value),
		displayValue: value.toLocaleString(),
	}));
	$: modeOptions = [...new Set([...supportedModes, currentMode].filter(Boolean))].map((value) => {
		const entry = SERIAL_MODE_LABELS[value];
		return { title: entry ? get(format)(entry.key, { default: entry.default }) : value, value };
	});
	$: formatOptions = [...new Set([...STANDARD_FORMATS, currentFormat])].map((v) => ({
		title: v,
		value: v,
	}));

	$: showModeField = modeOptions.length > 1;
	$: showHdToggle = currentHd !== undefined;
	$: showDeAlToggle = currentHd === true;

	const patchSerial = (patch: Partial<SerialConfig['serial']>) => {
		settings = { ...settings, serial: { ...settings.serial, ...patch } };
	};

	onMount(async () => {
		try {
			const res: SettingRequest = await gen2Request(device, 'Shelly.GetComponents', {
				keys: [component],
			});
			const entry = res?.data?.components?.find?.((c: { key?: string }) => c?.key === component);
			supportedModes = entry?.attrs?.modes ?? [];
		} catch {
			// Cached `settings` is good enough.
		}
	});
</script>

{#if showModeField}
	<Text styleType="h2" class="mb-s">
		{$format('label_serial_mode', { default: 'Mode' })}
	</Text>
	<Select
		class="mb-m"
		size="m"
		icon="fal fa-arrow-down-arrow-up"
		value={modeOptions.find((o) => o.value === currentMode) ?? modeOptions[0]}
		options={modeOptions}
		id="select-serial-mode"
		aria-label={$format('label_serial_mode', { default: 'Mode' })}
		on:change={(e) => (settings = { ...settings, mode: String(e.detail['value']) })}
	/>
{/if}

<SliderV2
	class="mb-m"
	id="slider-serial-baud"
	type="incremented"
	title={$format('label_serial_baud', { default: 'Baud rate' })}
	value={baudIndex}
	min={0}
	max={Math.max(0, baudValues.length - 1)}
	step={1}
	stopValueLabel
	lineMarkers={baudLineMarkers}
	a11yProps={{
		ariaLabel: $format('label_serial_baud', { default: 'Baud rate' }),
		ariaLabelValue: currentBaud.toLocaleString(),
	}}
	on:value_change={(e) => {
		const next = baudValues[Number(e.detail.value)];
		if (Number.isFinite(next)) patchSerial({ baud: next });
	}}
/>

<Text styleType="h2" class="mb-s">
	{$format('label_serial_format', { default: 'Format' })}
</Text>
<Select
	class="mb-m"
	size="m"
	icon="fal fa-list"
	value={formatOptions.find((o) => o.value === currentFormat) ?? formatOptions[0]}
	options={formatOptions}
	id="select-serial-format"
	aria-label={$format('label_serial_format', { default: 'Format' })}
	on:change={(e) => patchSerial({ format: String(e.detail['value']) })}
/>

{#if showHdToggle}
	<Text styleType="h2" class="mb-s">
		{$format('label_serial_transceiver_control', { default: 'Transceiver control' })}
	</Text>
	<Toggle
		class="mb-s"
		checked={!!currentHd}
		size="m"
		id="toggle-serial-hd"
		on:click={() => {
			const next = !currentHd;
			patchSerial(next ? { hd: true } : { hd: false, de_al: undefined });
		}}
	>
		{$format('label_serial_half_duplex', { default: 'Half-duplex' })}
	</Toggle>
{/if}

{#if showDeAlToggle}
	<Toggle
		class="mb-m"
		checked={!!currentDeAl}
		size="m"
		id="toggle-serial-de-al"
		on:click={() => patchSerial({ de_al: !currentDeAl })}
	>
		{$format('label_serial_de_active_high', { default: 'DE active high' })}
	</Toggle>
{/if}
